/**
 * The page behind the link in the confirmation email.
 *
 * It confirms on mount rather than behind a button, because the click already
 * happened — in the email. A second one would be asking the applicant to
 * confirm that they meant to confirm.
 *
 * But it is still a POST, and that is the whole reason this page exists instead
 * of the link pointing straight at the API. A confirmation link is followed by
 * whatever scans mail before a person sees it, and in a corporate mailbox that
 * is a link scanner: a GET endpoint would be spent by the scanner, leaving the
 * applicant a link that had already been used by the time they reached it.
 * Scanners do not run JavaScript.
 *
 * Strict Mode runs effects twice in development. The token is single-use, so
 * the second call answers 404 — guarded with a ref rather than by loosening the
 * single-use rule, which is the rule that stops a forwarded link working months
 * later.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Alert, Card, PageHeader } from "@/components/ui";
import { usePageMeta } from "@/lib/page-meta";
import { confirmApplication } from "@/lib/registry/applications";

const CONTEXT = "Pendaftaran vendor";

type State =
  | { status: "working" }
  | { status: "confirmed"; vendorName: string }
  | { status: "failed"; message: string };

export function VendorConfirmPage() {
  usePageMeta({ title: "Konfirmasi pendaftaran vendor" });

  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  // A missing token is knowable at first render — it is a fact about the URL,
  // not something to go and find out — so it is the initial state rather than
  // an effect that immediately corrects itself.
  const [state, setState] = useState<State>(() =>
    token
      ? { status: "working" }
      : {
          status: "failed",
          message: "Tautannya tidak lengkap. Buka lagi tautan di email persis seperti adanya.",
        },
  );
  const spent = useRef(false);

  useEffect(() => {
    if (!token || spent.current) return;
    spent.current = true;

    let cancelled = false;
    confirmApplication(token)
      .then((vendorName) => {
        if (!cancelled) setState({ status: "confirmed", vendorName });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: "failed",
            message:
              error instanceof Error ? error.message : "Konfirmasi tidak dapat diproses.",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="page-shell">
      <PageHeader
        context={CONTEXT}
        title={
          state.status === "confirmed" ? "Pendaftaran Anda sudah berjalan" : "Konfirmasi pendaftaran"
        }
        description={
          state.status === "confirmed"
            ? "Alamat email Anda terkonfirmasi, dan pengajuannya sudah masuk antrean persetujuan."
            : "Memeriksa tautan konfirmasi Anda."
        }
      />

      <Card title={state.status === "failed" ? "Tautan tidak berlaku" : "Status"}>
        {state.status === "working" ? <p className="text-sm text-ink-muted">Memproses…</p> : null}

        {state.status === "confirmed" ? (
          <div className="space-y-4 text-sm text-ink-muted">
            <p>
              Pengajuan untuk <strong className="text-ink">{state.vendorName}</strong> sekarang
              menunggu persetujuan pemegang kunci. Tidak ada lagi yang perlu Anda lakukan.
            </p>
            <p>
              Setelah disetujui, kontraknya memaksakan jeda aktivasi sebelum sertifikatnya bisa
              dicetak — jadi ini bukan proses hitungan menit. Sertifikat NFT-nya nanti dikirim
              ke alamat email ini juga, dan token-nya masuk ke wallet yang Anda cantumkan.
            </p>
            <Alert tone="warn">
              ASETQU tidak pernah meminta seed phrase, private key, atau tanda tangan wallet
              lewat email atau chat. Abaikan siapa pun yang memintanya.
            </Alert>
          </div>
        ) : null}

        {state.status === "failed" ? (
          <div className="space-y-4">
            <Alert tone="danger">{state.message}</Alert>
            <p className="text-sm text-ink-muted">
              Kalau tautannya sudah pernah dibuka, pengajuan Anda kemungkinan besar sudah
              berjalan dan tidak perlu dikonfirmasi lagi. Kalau belum pernah,{" "}
              <Link to="/daftar-vendor" className="underline decoration-dotted underline-offset-4">
                daftar ulang
              </Link>{" "}
              untuk mendapatkan tautan baru.
            </p>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
